import { Link } from "@tanstack/react-router";
import { ArrowRight, Clock } from "lucide-react";
import {
  articlesForTopic,
  matchedKeywords,
  type HubTopic,
} from "@/lib/topic-keywords";
import type { CountrySlug } from "@/data/countries";

interface Props {
  topic: HubTopic;
  country?: CountrySlug;
  countryLabel?: string;
  limit?: number;
  /** Heading level used for the block title. */
  as?: "h2" | "h3";
}

/**
 * KeywordNavBlock — an automatically populated internal-navigation block.
 * It matches published articles to a hub topic (and optional country) by
 * keyword, so every newly published guide is linked from the country hub and
 * the Immigration / Jobs / Finance / Tax subpages with zero manual work.
 */
export function KeywordNavBlock({
  topic,
  country,
  countryLabel,
  limit = 6,
  as = "h3",
}: Props) {
  const articles = articlesForTopic(topic, country, limit);
  if (articles.length === 0) return null;

  const label = countryLabel ? `${countryLabel} ${topic}` : topic;
  const Heading = as;

  return (
    <section className="mt-10 rounded-2xl border border-accent/25 bg-card/60 p-5 shadow-sm backdrop-blur sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <Heading className="font-display text-xl font-bold text-accent sm:text-2xl">
          {label} guides
        </Heading>
        <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          Keyword-matched
        </span>
      </div>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {articles.map((a) => {
          const keywords = matchedKeywords(a, topic);
          return (
            <li key={a.href}>
              <Link
                to={a.href}
                title={`Read: ${a.title}`}
                className="group flex h-full flex-col rounded-xl border border-border bg-background/40 p-4 transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-md"
              >
                <span className="font-semibold leading-snug text-foreground group-hover:text-accent">
                  {a.title}
                </span>
                <span className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                  {a.description}
                </span>
                <span className="mt-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {keywords.map((k) => (
                    <span
                      key={k}
                      className="rounded-full border border-accent/30 px-2 py-0.5 text-accent"
                    >
                      {k}
                    </span>
                  ))}
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" aria-hidden /> {a.readMinutes} min
                  </span>
                </span>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-accent">
                  Read guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** All four topic blocks for one country hub. */
export function CountryTopicNav({
  country,
  countryLabel,
}: {
  country: CountrySlug;
  countryLabel: string;
}) {
  return (
    <div>
      {(["Immigration", "Jobs", "Finance", "Tax"] as HubTopic[]).map((t) => (
        <KeywordNavBlock
          key={t}
          topic={t}
          country={country}
          countryLabel={countryLabel}
          limit={4}
        />
      ))}
    </div>
  );
}
